import React, { useState } from "react";
import { toast } from "react-toastify";
import Text from "@components/Text";
import Button from "@components/Button";
import Dialog from "@components/Dialog";
import { Column, Row } from "@src/components/Flex";
import CreateSeasonDialog from "@components/Layout/Seasons/CreateSeasonDialog";
import { useLang } from "@src/hooks/useLang";
import { useSeasonsVM } from "@src/stores/SeasonStore";

/**
 * Gate field-creation actions behind "the user has a season". Without a season
 * the backend creates an orphan field with no crop/boundary, so the user is
 * told what seasons are for, offered to create one, and the action they asked
 * for runs once it exists. Moved out of ViewFieldsMenu.
 *
 * `requireSeason(action)` returns true when the action may go ahead now; render
 * `seasonDialogs` once, anywhere in the caller.
 */
export function useRequireSeason<A>(hasSeason: boolean, run: (action: A) => void) {
  const lang = useLang();
  const seasonsVM = useSeasonsVM();
  const [promptVisible, setPromptVisible] = useState(false);
  const [createVisible, setCreateVisible] = useState(false);
  const [pending, setPending] = useState<A | null>(null);

  const requireSeason = (action: A): boolean => {
    if (hasSeason) return true;
    setPending(action);
    setPromptVisible(true);
    return false;
  };

  const seasonDialogs = (
    <>
      <Dialog
        title="Create a season first"
        visible={promptVisible}
        onClose={() => { setPromptVisible(false); setPending(null); }}
        style={{ maxWidth: 440 }}
      >
        <Column crossAxisSize="max" style={{ gap: 12 }}>
          <Text size={14} color="#494C5B">
            Seasons keep different harvests on the same field separate from
            each other and stored in history, so you can compare yield year
            over year. Create your first season to continue.
          </Text>
          <Row justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
            <Button
              kind="outline"
              onClick={() => { setPromptVisible(false); setPending(null); }}
            >
              {lang.seasons.cancelButton}
            </Button>
            <Button
              onClick={() => { setPromptVisible(false); setCreateVisible(true); }}
            >
              {lang.seasons.createSeasonButton}
            </Button>
          </Row>
        </Column>
      </Dialog>
      <CreateSeasonDialog
        visible={createVisible}
        seasons={seasonsVM.seasons}
        onSubmit={(from, to, copyFromSeasonId) => {
          seasonsVM
            .createSeason(from, to, copyFromSeasonId)
            .then(() => {
              setCreateVisible(false);
              const action = pending;
              setPending(null);
              if (action != null) run(action);
            })
            .catch((e) => {
              toast.error(e.message ?? e.toString());
            });
        }}
        onClose={() => { setCreateVisible(false); setPending(null); }}
      />
    </>
  );

  return { requireSeason, seasonDialogs };
}
